import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStyletron } from 'baseui';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';
import { ChatItem, Role } from '../chat-item/ChatItem';
import { Placeholder } from '@relaynetwork/design-system';
import { UserStatusBanner } from '../user-status-badge/UserStatusBadge';
import { ConversationsClicked } from '../relay-messenger-conversations/RelayMessengerConversations';
import { useCustomerInfo } from '../../context/CustomerInfoContext';
import { Banner, KIND } from 'baseui/banner';
import { TwoWayService } from '../relay-messenger-axios-service/relay-messenger-axios-service';

export type TwoWayChatMessage = {
  id?: string;
  author: string;
  timestamp: string;
  body: string;
  displayName: string;
  versions?: TwoWayChatMessageVersion;
};

export type TwoWayChatMessageVersion = {
  original: string;
  originalLanguageCode: string;
  translations: {
    [key: string]: {
      languageCode: string;
      text: string;
      edited: boolean;
    };
  };
};

export type ClientStatus = {
  type: 'BUSINESS_HOURS' | 'OUT_OF_OFFICE' | 'AFTER_HOURS';
  expires: string;
};

export type MessageSendingStatus =
  | 'notSent'
  | 'inProgress'
  | 'success'
  | 'failed';

export type TwoWayChatCardProps = {
  chatMessages: TwoWayChatMessage[];
  isJoinError?: boolean;
  errorMessage?: React.ReactNode;
  isOtherUserTyping?: boolean;
  typingMessage?: string;
  messageSendingStatus?: MessageSendingStatus;
  onTyping?: () => void;
  conversationsClicked: ConversationsClicked;
  lastMessageLoaded: boolean;
  clientId: string;
  isRMAutoResponseEnabled: boolean;
  isRMTranslationsEnabled?: boolean;
  isConversationTranslated?: boolean;
  onTranslateConversation?: () => void;
  enableTranslationError?: { error: boolean; message: string };
  composerRef?: React.RefObject<HTMLDivElement>;
};

export type ChatTextProps = {
  handleInputChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  onTyping?: () => void;
  messageVal: string;
  twoWayChatLoading: boolean;
};

export type TwoWayChatSkelProps = {
  alignment: string;
};

export function checkAuthorIsAgent(author: string) {
  return author && author.split('::')[0] === 'agent';
}

const TwoWayChatSkeleton = ({ alignment = 'left' }: TwoWayChatSkelProps) => {
  const [css] = useStyletron();
  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignment === 'left' ? 'start' : 'end',
        marginTop: '20px',
        bottom: '10px',
      })}
    >
      <Placeholder
        height="12vh"
        width="70vw"
        borderRadius="15px"
        animation={true}
      ></Placeholder>
      <br />
      <Placeholder
        rows={2}
        height="4vh"
        width="30vw"
        borderRadius="10px"
        animation={true}
      ></Placeholder>
    </div>
  );
};

export const TwoWayChatCard = ({
  chatMessages,
  isJoinError,
  errorMessage,
  typingMessage = '',
  conversationsClicked: {
    IsMyClosedConversationsClicked,
    isMyConversationsClicked,
  },
  lastMessageLoaded = false,
  clientId,
  isRMAutoResponseEnabled,
  isRMTranslationsEnabled = false,
  isConversationTranslated = false,
  onTranslateConversation = () => {},
  enableTranslationError = { error: false, message: '' },
  composerRef,
}: TwoWayChatCardProps) => {
  const [css, theme] = useStyletron();
  const customTheme = theme as CustomTheme;
  const skeletonCount = 5;
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState(chatMessages);
  const [twoWayChatLoading, setTwoWayChatLoading] = useState(false);
  const [scrollFlag, setScrollFlag] = useState(false);
  const isAtBottomRef = useRef(true);
  const {
    customerInfo: { closedReason, closedReasonAdditionalDetail },
  } = useCustomerInfo();
  const [clientStatus, setClientStatus] = useState<ClientStatus | null>(null);
  // identify the most recent customer message in the index to show "enable translation" option only for that message
  const [lastCustomerMessageIndex, setLastCustomerMessageIndex] = useState<
    number | null
  >(null);

  const getClientStatus = async (clientId: string) => {
    if (isRMAutoResponseEnabled) {
      const status = await TwoWayService.getClientStatus(clientId);
      if (status) setClientStatus(status as ClientStatus);
    }
  };
  const setClientStatusMessage = (date: string) => {
    if (!date)
      return `The After Hours auto response is enabled indefinitely. Update your business hours to reflect availability in Relay Messenger.`;
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const expirationDate = new Date(date);

    if (
      expirationDate.getDate() === tomorrow.getDate() &&
      expirationDate.getMonth() === tomorrow.getMonth() &&
      expirationDate.getFullYear() === tomorrow.getFullYear()
    ) {
      return `Customers are receiving an automatic reply until ${expirationDate.toLocaleTimeString(
        'en-US',
        { hour: '2-digit', minute: '2-digit', hour12: true },
      )} tomorrow`;
    } else {
      return `Customers are receiving an automatic reply until ${expirationDate.toLocaleDateString()} at ${expirationDate.toLocaleTimeString(
        'en-US',
        {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZoneName: 'short',
        },
      )}`;
    }
  };

  useEffect(() => {
    setScrollFlag(false);
    setMessages(chatMessages);
  }, [chatMessages]);

  useEffect(() => {
    if (!IsMyClosedConversationsClicked) {
      getClientStatus(clientId);
    }
  }, [isMyConversationsClicked, IsMyClosedConversationsClicked]);

  useEffect(() => {
    if (!scrollFlag && twoWayChatLoading) {
      setScrollFlag(true);
      const scrollHeight = messagesContainerRef.current.scrollHeight;
      const height = messagesContainerRef.current.clientHeight;
      const maxScrollTop = scrollHeight - height;
      messagesContainerRef.current.scrollTop =
        maxScrollTop > 0 ? maxScrollTop : 0;
      isAtBottomRef.current = true;
    }
  }, [twoWayChatLoading, scrollFlag, typingMessage, isConversationTranslated]);

  const prevComposerHeightRef = useRef(0);
  useEffect(() => {
    const composerEl = composerRef?.current;
    const messagesEl = messagesContainerRef.current;
    if (!composerEl || !messagesEl) return;

    prevComposerHeightRef.current = composerEl.offsetHeight;

    const observer = new ResizeObserver(() => {
      const newHeight = composerEl.offsetHeight;
      const delta = newHeight - prevComposerHeightRef.current;

      if (delta !== 0) {
        if (isAtBottomRef.current) {
          messagesEl.scrollTop = messagesEl.scrollHeight;
        } else {
          messagesEl.scrollTop += delta;
        }
      }

      prevComposerHeightRef.current = newHeight;
    });

    observer.observe(composerEl);
    return () => observer.disconnect();
  }, [composerRef]);

  useEffect(() => {
    setTwoWayChatLoading(lastMessageLoaded);
  }, [lastMessageLoaded]);

  useEffect(() => {
    setScrollFlag(false);
  }, [isConversationTranslated]);

  useEffect(() => {
    if (messages?.length > 0) {
      const lastIndex = findLastIndex(
        messages,
        (message) =>
          !checkAuthorIsAgent(message.author) && message.author != 'system',
      );
      if (lastIndex !== -1) setLastCustomerMessageIndex(lastIndex);
    }
  }, [messages]);

  const findLastIndex = (array, predicate) => {
    for (let i = array.length - 1; i >= 0; i--) {
      if (predicate(array[i], i, array)) return i;
    }
    return -1;
  };
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    isAtBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (el.scrollTop === 0) {
      document.documentElement.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  return (
    <div
      ref={messagesContainerRef}
      onScroll={handleScroll}
      className={css({
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto', // makes the messages container scrollable
        overflowAnchor: 'none', // disables the browser’s scroll anchoring behavior
        backgroundColor: customTheme.colors.white,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        minHeight: 0,
      })}
    >
      {isJoinError ? (
        <div>{errorMessage}</div>
      ) : (
        <>
          <main
            className={css({
              paddingLeft: '1rem',
              paddingRight: '1rem',
            })}
            key={messages.length}
          >
            {!twoWayChatLoading && (
              <div>
                {Array.from(Array(skeletonCount), (el, index) => {
                  return (
                    <TwoWayChatSkeleton
                      alignment={index % 2 ? 'right' : 'left'}
                      key={index}
                    ></TwoWayChatSkeleton>
                  );
                })}
              </div>
            )}
            {messages &&
              twoWayChatLoading &&
              messages.map((message, index) => {
                return (
                  <div
                    key={index}
                    data-testid={`chat-item-${index}`}
                    className={css({
                      maxWidth: '75%',
                      marginTop: '1rem',
                      marginBottom: '1rem',
                      ...(!checkAuthorIsAgent(message.author)
                        ? {
                            marginRight: 'auto',
                          }
                        : {
                            marginLeft: 'auto',
                          }),
                    })}
                  >
                    <ChatItem
                      stampVariant={'user-time'}
                      user={message.displayName}
                      timestamp={message.timestamp}
                      dataTestId={`chat-bubble-${index}`}
                      role={
                        checkAuthorIsAgent(message.author)
                          ? Role.Agent
                          : Role.Customer
                      }
                      text={
                        checkAuthorIsAgent(message.author)
                          ? Object.values(
                              message?.versions?.translations || {},
                            )[0]?.text || message.body
                          : message.body
                      }
                      translatedLanguageCode={
                        checkAuthorIsAgent(message.author)
                          ? Object.values(
                              message?.versions?.translations || {},
                            )[0]?.languageCode
                          : message.versions?.originalLanguageCode
                      }
                      hasMessageTranslationError={
                        message.versions?.originalLanguageCode === 'unknown'
                      }
                      translatedText={
                        !isConversationTranslated || !isRMTranslationsEnabled
                          ? undefined
                          : checkAuthorIsAgent(message.author)
                          ? message.body
                          : message.versions?.translations?.['en']?.text
                      }
                      showEnableTranslationOption={
                        index === lastCustomerMessageIndex &&
                        isMyConversationsClicked
                      }
                      isTranslationEnabled={isConversationTranslated}
                      onEnableTranslationClick={onTranslateConversation}
                      isRMTranslationsEnabled={isRMTranslationsEnabled}
                      enableTranslationError={enableTranslationError}
                    ></ChatItem>
                  </div>
                );
              })}

            {typingMessage && (
              <div className="two-way-typing-prompt">
                {/* the 3 <span> elements represent the 3 loading animations  */}
                <span></span>
                <span></span>
                <span></span>
                {/* end of code for the typing prompt animation  */}
              </div>
            )}
            {isConversationTranslated && isRMTranslationsEnabled && (
              <UserStatusBanner
                content="This conversation is being translated."
                variant="secondary"
              ></UserStatusBanner>
            )}
            {isRMAutoResponseEnabled &&
              clientStatus &&
              (clientStatus.type === 'OUT_OF_OFFICE' ||
                clientStatus.type === 'AFTER_HOURS') &&
              twoWayChatLoading && (
                <Banner
                  title={
                    clientStatus.type === 'AFTER_HOURS'
                      ? `It's after hours.`
                      : 'The Out of Office auto response is on.'
                  }
                  kind={KIND.warning}
                  overrides={{
                    Title: {
                      style: {
                        textAlign: 'center',
                      },
                    },
                    Message: {
                      style: {
                        textAlign: 'center',
                        fontStyle: 'italic',
                      },
                    },
                  }}
                >
                  {`${setClientStatusMessage(clientStatus.expires)}`}
                </Banner>
              )}
          </main>

          {IsMyClosedConversationsClicked && (
            <>
              <UserStatusBanner
                content="This conversation has been closed."
                variant="secondary"
              ></UserStatusBanner>
              {closedReason && (
                <UserStatusBanner
                  content={closedReason}
                  secondaryContent={closedReasonAdditionalDetail}
                  variant="secondary"
                ></UserStatusBanner>
              )}
              <p
                className={css({
                  fontSize: '12px',
                  color: '#908E8F',
                  textAlign: 'center',
                  marginTop: '10px',
                })}
              >
                You can no longer respond to this conversation unless the
                customer re-opens it.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
};
